import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { API_BASE_URL as API } from "../apiConfig";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  AlertTriangle,
  CircleHelp,
  Sparkles,
  Wand2,
  Plane,
  Car,
} from "lucide-react";
import {
  AIRPORT_TERMINALS,
  ALL_VEHICLE_TYPES,
  EXTRA_SERVICES_CONFIG,
  getLocalizedAreaName,
  LABELS,
  VEHICLE_TYPES,
} from "./new-order-form-v2/constants";
import FormNavigation from "./new-order-form-v2/FormNavigation";
import { openDatePicker } from "./new-order-form-v2/openDatePicker";
import PickupAreaStep from "./new-order-form-v2/PickupAreaStep";
import ServiceTypeStep from "./new-order-form-v2/ServiceTypeStep";
import StepProgress from "./new-order-form-v2/StepProgress";
import VehicleStep from "./new-order-form-v2/VehicleStep";
import { continuousBookingCopy, charterFieldCopy, airportFlightCopy } from './new-order-form-v2/continuousBookingCopy';
import { isAirportArea, appendAirportFlightNotes } from './new-order-form-v2/airportFlights';
import { setExtendedWaitingQuantity } from '../utils/extendedWaiting';
import { bookingRead, bookingErrorMessage, bookingMessages, reportBookingFailure } from "./new-order-form-v2/bookingRequest";

const isExtraServiceVisibleForVehicle = (service, vehicleType) => (
  !service.vehicleTypes || service.vehicleTypes.includes(vehicleType)
);

const normalizeCustomerProfileCode = (value) => String(value || "").trim().toLowerCase();
const createIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `order-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const createVehiclePlan = (source = {}) => ({
  id: globalThis.crypto?.randomUUID?.() || `vehicle-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  quantity: Math.max(1, Number(source.quantity) || 1),
  vehicle_type: source.vehicle_type || "alphard",
  passenger_count: Math.max(1, Number(source.passenger_count) || 1),
  luggage_count: Math.max(0, Number(source.luggage_count) || 0),
  child_seat_count: Math.max(0, Number(source.child_seat_count) || 0),
  extra_services: Array.isArray(source.extra_services) ? source.extra_services.map(service => ({ ...service })) : [],
  other_service_desc: source.other_service_desc || "",
  trip_details: source.trip_details || "",
});

const getOrderServiceTime = (order) => {
  const date = order?.departure_date || "";
  const time = order?.pickup_time || "00:00";
  return new Date(`${date}T${time}`).getTime() || 0;
};

const getMostFrequentValue = (values) => {
  const counts = new Map();
  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));

  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value]) => value)[0] || "";
};

const resolveCustomerPaymentHabitKey = (orders) => {
  if (orders.some((order) => (
    order.credit_card_payment ||
    order.square_payment_status === "paid" ||
    /card|credit|square|カード|信用卡/i.test(String(order.payment_method || ""))
  ))) {
    return "card";
  }
  if (orders.some((order) => order.payment_already_received || order.fee_paid)) {
    return "paid";
  }
  if (orders.some((order) => String(order.payment_method || "").trim())) {
    return "recorded";
  }
  return "unknown";
};

export const buildCustomerAutofillProfile = (orders, customerCode) => {
  const code = normalizeCustomerProfileCode(customerCode);
  if (!code) return null;

  const matchedOrders = (orders || [])
    .filter((order) => normalizeCustomerProfileCode(order.customer_code) === code)
    .sort((a, b) => getOrderServiceTime(b) - getOrderServiceTime(a));

  if (!matchedOrders.length) return null;

  return {
    code,
    orderCount: matchedOrders.length,
    customer_name: getMostFrequentValue(matchedOrders.map((order) => order.customer_name)),
    phone: getMostFrequentValue(matchedOrders.map((order) => order.phone || order.customer_phone)),
    customer_email: getMostFrequentValue(matchedOrders.map((order) => order.customer_email)),
    vehicle_type: getMostFrequentValue(matchedOrders.map((order) => order.vehicle_type)),
    pickup_location_detail: getMostFrequentValue(matchedOrders.map((order) => order.pickup_location_detail || order.pickup_location)),
    dropoff_location_detail: getMostFrequentValue(matchedOrders.map((order) => order.dropoff_location_detail || order.destination)),
    frequent_hotel: getMostFrequentValue(matchedOrders.flatMap((order) => [
      order.pickup_location_detail,
      order.dropoff_location_detail,
      order.pickup_location,
      order.destination,
    ])),
    paymentHabitKey: resolveCustomerPaymentHabitKey(matchedOrders),
  };
};

export default function NewOrderFormV2({ user, onOrderCreated, orders = [], onPreviewChange, continuousLayout = false, actionsRef }) {
  const { language } = useLanguage();
  const t = LABELS[language] || LABELS.zh;
  
  // Steps
  const [currentStep, setCurrentStep] = useState(1);
  
  // Configuration from backend
  const [areas, setAreas] = useState([]);
  const [endLocationOptions, setEndLocationOptions] = useState(null);
  const [configError, setConfigError] = useState("");
  const [endLocationError, setEndLocationError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [configLoading, setConfigLoading] = useState(false);
  const endLocationRequestRef = useRef(0);
  const busyRef = useRef(false);
  const requestCopy = bookingMessages[language] || bookingMessages.en;
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1
    pickup_area: "",
    pickup_area_other: "",
    pickup_terminal: "", // For airport terminal selection
    // Step 2
    service_type: "", // transfer or charter
    // Step 3
    departure_date: "",
    pickup_time: "",
    pickup_location_detail: "",
    dropoff_location_detail: "",
    dropoff_area: "",
    dropoff_terminal: "", // For airport terminal selection
    flight_number: "",
    pickup_flight_number: "",
    end_flight_number: "",
    passenger_count: 1,
    luggage_count: 0,
    child_seat_count: 0,
    selected_hours: 5,
    // New charter end location fields
    end_location_in_base: true, // Whether end location is in base (Tokyo/Kanazawa)
    end_location: "", // End location code
    end_location_other: "", // Custom end location text
    extra_services: [],
    other_service_desc: "",
    // Step 4
    vehicle_type: "alphard",
    // Customer info (not supplier info)
    customer_code: "",
    customer_name: "",
    phone: "",
    trip_details: "",
  });
  
  // Price calculation result
  const [priceSnapshot, setPriceSnapshot] = useState(null);
  const [vehiclePlans, setVehiclePlans] = useState(() => [createVehiclePlan(formData)]);
  const [vehiclePriceSnapshots, setVehiclePriceSnapshots] = useState({});
  const priceRequestSequence = useRef(0);
  const priceRequestKey = JSON.stringify([formData.service_type,formData.pickup_area,formData.dropoff_area,formData.departure_date,formData.pickup_time,formData.selected_hours,formData.end_location,formData.end_location_in_base,formData.end_location_other,vehiclePlans.map(v=>[v.id,v.quantity,v.vehicle_type,v.child_seat_count,v.extra_services])]);
  const currentPriceKeyRef = useRef(priceRequestKey);
  currentPriceKeyRef.current = priceRequestKey;
  const [createCombinedPaymentPage, setCreateCombinedPaymentPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionKeyRef = useRef(null);
  const [aiRawText, setAiRawText] = useState("");
  const [aiParsing, setAiParsing] = useState(false);
  const [aiParseResult, setAiParseResult] = useState(null);
  const [aiParseError, setAiParseError] = useState("");
  const [autofillNotice, setAutofillNotice] = useState(null);
  const [customerAutofillSelections, setCustomerAutofillSelections] = useState({});

  const customerAutofillProfile = useMemo(
    () => buildCustomerAutofillProfile(orders, formData.customer_code),
    [orders, formData.customer_code]
  );

  const expandedVehicleCount = useMemo(
    () => vehiclePlans.reduce((total, vehicle) => total + Math.max(1, Number(vehicle.quantity) || 1), 0),
    [vehiclePlans]
  );

  const calculatedMultiVehicleTotal = useMemo(
    () => vehiclePlans.reduce((total, vehicle) => {
      const snapshot = vehiclePriceSnapshots[vehicle.id];
      return total + (Number(snapshot?.total_price) || 0) * Math.max(1, Number(vehicle.quantity) || 1);
    }, 0),
    [vehiclePlans, vehiclePriceSnapshots]
  );

  const multiVehiclePriceReady = vehiclePlans.length > 0 && vehiclePlans.every(vehicle => {
    const snapshot = vehiclePriceSnapshots[vehicle.id];
    return snapshot && !snapshot.requires_quote && Number(snapshot.total_price) > 0;
  });
  const isMultiVehicleBooking = expandedVehicleCount > 1 || vehiclePlans.length > 1;
  const submissionPriceReady = isMultiVehicleBooking ? multiVehiclePriceReady : Boolean(priceSnapshot);

  useEffect(() => {
    const normalizedCode = normalizeCustomerProfileCode(formData.customer_code);
    if (!normalizedCode) {
      setAutofillNotice(null);
      setCustomerAutofillSelections({});
      return;
    }

    setAutofillNotice(customerAutofillProfile || null);
    setCustomerAutofillSelections({});
  }, [customerAutofillProfile, formData.customer_code]);

  useEffect(() => {
    setFormData(prev => {
      const filteredServices = prev.extra_services.filter(svc => isExtraServiceVisibleForVehicle(
        EXTRA_SERVICES_CONFIG.find(service => service.id === svc.service_type) || {},
        prev.vehicle_type
      ));
      if (filteredServices.length === prev.extra_services.length) {
        return prev;
      }
      return { ...prev, extra_services: filteredServices };
    });
  }, [formData.vehicle_type]);

  useEffect(() => {
    setVehiclePlans(prev => {
      if (!prev.length) return [createVehiclePlan()];
      const first = prev[0];
      const nextFirst = {
        ...first,
        vehicle_type: formData.vehicle_type || first.vehicle_type,
        passenger_count: Number(formData.passenger_count) || 1,
        luggage_count: Math.max(0, Number(formData.luggage_count) || 0),
        child_seat_count: Math.max(0, Number(formData.child_seat_count) || 0),
        extra_services: Array.isArray(formData.extra_services) ? formData.extra_services.map(service => ({ ...service })) : [],
        other_service_desc: formData.other_service_desc || "",
        trip_details: formData.trip_details || "",
      };
      return [nextFirst, ...prev.slice(1)];
    });
  }, [
    formData.vehicle_type,
    formData.passenger_count,
    formData.luggage_count,
    formData.child_seat_count,
    formData.extra_services,
    formData.other_service_desc,
    formData.trip_details,
  ]);

  const updateVehiclePlan = (vehicleId, updates) => {
    setVehiclePlans(prev => prev.map((vehicle, index) => {
      if (vehicle.id !== vehicleId) return vehicle;
      const nextVehicle = { ...vehicle, ...updates };
      if (index === 0) {
        setFormData(current => ({
          ...current,
          ...Object.fromEntries([
            "vehicle_type",
            "passenger_count",
            "luggage_count",
            "child_seat_count",
            "extra_services",
            "other_service_desc",
            "trip_details",
          ].filter(key => Object.prototype.hasOwnProperty.call(updates, key)).map(key => [key, nextVehicle[key]])),
        }));
      }
      return nextVehicle;
    }));
    setVehiclePriceSnapshots({});
    setPriceSnapshot(null);
    submissionKeyRef.current = null;
  };

  const addVehiclePlan = () => {
    if (expandedVehicleCount >= 30) return;
    setVehiclePlans(prev => [...prev, createVehiclePlan(prev[prev.length - 1] || formData)]);
    setVehiclePriceSnapshots({});
  };

  const duplicateVehiclePlan = (vehicleId) => {
    if (expandedVehicleCount >= 30) return;
    setVehiclePlans(prev => {
      const source = prev.find(vehicle => vehicle.id === vehicleId);
      return source ? [...prev, createVehiclePlan({ ...source, quantity: 1 })] : prev;
    });
    setVehiclePriceSnapshots({});
  };

  const removeVehiclePlan = (vehicleId) => {
    setVehiclePlans(prev => prev.length > 1 ? prev.filter(vehicle => vehicle.id !== vehicleId) : prev);
    setVehiclePriceSnapshots({});
    setPriceSnapshot(null);
  };

  // Reset dropoff_area when pickup_area changes to a different region
  useEffect(() => {
    if (areas.length > 0 && formData.pickup_area && formData.dropoff_area) {
      // Get the pickup area's region
      const pickupArea = areas.find(a => a.code === formData.pickup_area);
      const dropoffArea = areas.find(a => a.code === formData.dropoff_area);
      
      if (pickupArea && dropoffArea) {
        const pickupRg = pickupArea.rule_group || "";
        const dropoffRg = dropoffArea.rule_group || "";
        
        // Normalize regions
        const normalizeRegion = (rg) => {
          if (rg.includes("kansai") || rg.includes("osaka")) return "kansai";
          if (rg.includes("kanazawa")) return "kanazawa";
          if (rg.includes("okinawa")) return "okinawa";
          return "kanto";
        };
        
        const pickupRegion = normalizeRegion(pickupRg);
        const dropoffRegion = normalizeRegion(dropoffRg);
        
        // If regions don't match, reset dropoff_area
        if (pickupRegion !== dropoffRegion) {
          setFormData(prev => ({ 
            ...prev, 
            dropoff_area: "", 
            dropoff_area_other: "",
            end_location: "",
            end_location_other: ""
          }));
        }
      }
    }
  }, [formData.pickup_area, formData.dropoff_area, areas]);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError("");
    try {
      const areasRes = await bookingRead(`${API}/config/areas`);
      if (!Array.isArray(areasRes.data) || !areasRes.data.length) throw new Error("Invalid area response");
      setAreas(areasRes.data.filter(a => a.enabled !== false));
    } catch (error) {
      console.error("Failed to fetch config", error);
      reportBookingFailure("areas", error);
      setConfigError(bookingErrorMessage(error, language));
    } finally {
      setConfigLoading(false);
    }
  }, [language]);

  const fetchEndLocationOptions = useCallback(async (pickupArea) => {
    const requestId = ++endLocationRequestRef.current;
    setEndLocationError("");
    try {
      const response = await bookingRead(`${API}/config/end-locations/${pickupArea}`);
      if (!Array.isArray(response.data?.in_base_locations)) throw new Error("Invalid end location response");
      if (requestId === endLocationRequestRef.current) setEndLocationOptions(response.data);
    } catch (error) {
      console.error("Failed to fetch end location options", error);
      reportBookingFailure("end_locations", error);
      if (requestId === endLocationRequestRef.current) setEndLocationError(bookingErrorMessage(error, language));
    }
  }, [language]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => {
    const counter = endLocationRequestRef;
    counter.current += 1;
    setEndLocationOptions(null);
    setEndLocationError("");
    if (formData.pickup_area && formData.service_type === "charter") {
      fetchEndLocationOptions(formData.pickup_area);
    }
    return () => { counter.current += 1; };
  }, [formData.pickup_area, formData.service_type, fetchEndLocationOptions]);

  // Get the base code for the currently selected pickup area
  const getSelectedBase = () => {
    if (!formData.pickup_area || formData.pickup_area === "other") {
      return "tokyo"; // Default
    }
    const area = areas.find(a => a.code === formData.pickup_area);
    if (!area) return "tokyo";
    
    // Determine base from rule_group or allowed_bases
    const allowedBases = area.allowed_bases;
    if (allowedBases && allowedBases.length > 0) {
      return allowedBases[0]; // Use first allowed base
    }
    
    // Fallback: derive from rule_group
    const rg = area.rule_group || "";
    if (rg.includes("kansai") || rg.includes("osaka")) return "osaka";
    if (rg.includes("kanazawa")) return "kanazawa";
    if (rg.includes("okinawa")) return "okinawa";
    return "tokyo";
  };

  const isKansaiPremiumHiaceCharter = (
    formData.service_type === "charter" &&
    formData.vehicle_type === "premium_hiace" &&
    getSelectedBase() === "osaka"
  );

  useEffect(() => {
    if (isKansaiPremiumHiaceCharter && Number(formData.selected_hours) !== 10) {
      setFormData(prev => ({ ...prev, selected_hours: 10 }));
    }
  }, [isKansaiPremiumHiaceCharter, formData.selected_hours]);

  // Get available vehicle types based on selected base
  const getAvailableVehicleTypes = () => {
    return ALL_VEHICLE_TYPES;
  };

  const visibleExtraServices = EXTRA_SERVICES_CONFIG.filter(service =>
    !service.vehicleTypes || service.vehicleTypes.includes(formData.vehicle_type)
  );

  // Get destination areas filtered by pickup area's region (rule_group)
  const getDestinationAreasByBase = () => {
    if (!formData.pickup_area || formData.pickup_area === "other") {
      return areas; // If no pickup area selected, show all
    }
    
    // Get the pickup area's rule_group
    const pickupArea = areas.find(a => a.code === formData.pickup_area);
    if (!pickupArea) return areas;
    
    const pickupRuleGroup = pickupArea.rule_group || "";
    
    // Normalize the rule_group to a region identifier
    let pickupRegion;
    if (pickupRuleGroup.includes("kansai") || pickupRuleGroup.includes("osaka")) {
      pickupRegion = "kansai";
    } else if (pickupRuleGroup.includes("kanazawa")) {
      pickupRegion = "kanazawa";
    } else if (pickupRuleGroup.includes("okinawa")) {
      pickupRegion = "okinawa";
    } else {
      // Default: kanto
      pickupRegion = "kanto";
    }
    
    // Filter areas by matching region
    return areas.filter(area => {
      const areaRuleGroup = area.rule_group || "";
      
      if (pickupRegion === "kansai") {
        return areaRuleGroup.includes("kansai") || areaRuleGroup.includes("osaka");
      } else if (pickupRegion === "kanazawa") {
        return areaRuleGroup.includes("kanazawa");
      } else if (pickupRegion === "okinawa") {
        return areaRuleGroup.includes("okinawa");
      } else {
        // Kanto region
        return areaRuleGroup.includes("kanto") || areaRuleGroup.includes("tokyo");
      }
    });
  };

  // Calculate price when moving to step 5
  const calculatePrice = async (background = false) => {
    const sequence = ++priceRequestSequence.current;
    const requestKey = currentPriceKeyRef.current;
    if (!background) setLoading(true);
    setRequestError("");
    try {
      const buildRequestData = (vehicle) => ({
        service_type: formData.service_type,
        pickup_area: formData.pickup_area === "other" ? "tokyo" : formData.pickup_area,
        dropoff_area: formData.service_type === "charter" 
          ? (formData.dropoff_area || formData.pickup_area)
          : (formData.dropoff_area || formData.pickup_area),
        departure_date: formData.departure_date,
        pickup_time: formData.pickup_time || null,
        selected_hours: formData.service_type === "charter" ? formData.selected_hours : null,
        // New end location fields for charter
        end_location: formData.service_type === "charter" ? formData.end_location : null,
        end_location_in_base: formData.service_type === "charter" ? formData.end_location_in_base : true,
        end_location_other: formData.service_type === "charter" ? formData.end_location_other : null,
        vehicle_type: vehicle.vehicle_type,
        child_seat_count: vehicle.child_seat_count,
        extra_services: vehicle.extra_services.map(svc => ({
          service_type: svc.service_type,
          price: EXTRA_SERVICES_CONFIG.find(c => c.id === svc.service_type)?.price || 0,
          quantity: svc.quantity || 1,
          description: svc.description,
        })),
      });

      const responses = await Promise.all(
        vehiclePlans.map(vehicle => bookingRead(`${API}/calculate-price`, buildRequestData(vehicle)))
      );
      if (sequence !== priceRequestSequence.current || requestKey !== currentPriceKeyRef.current) return false;
      if (responses.some(response => !response.data || typeof response.data !== "object"
        || (!response.data.requires_quote && !Number.isFinite(response.data.total_price)))) {
        throw new Error("Invalid price response");
      }
      const snapshots = Object.fromEntries(
        vehiclePlans.map((vehicle, index) => [vehicle.id, responses[index].data])
      );
      setVehiclePriceSnapshots(snapshots);
      setPriceSnapshot(responses[0]?.data || null);
      return true;
    } catch (error) {
      if (sequence !== priceRequestSequence.current || requestKey !== currentPriceKeyRef.current) return false;
      console.error("Failed to calculate price", error);
      reportBookingFailure("price", error);
      setVehiclePriceSnapshots({});
      setPriceSnapshot(null);
      setRequestError(bookingErrorMessage(error, language));
      return false;
    } finally {
      if (!background) setLoading(false);
    }
  };
  const calculatePreviewRef = useRef(calculatePrice);
  calculatePreviewRef.current = calculatePrice;
  useEffect(() => {
    if (!continuousLayout || currentStep === 5) return;
    setPriceSnapshot(null); setVehiclePriceSnapshots({});
    if (!formData.pickup_area || !formData.service_type || !formData.departure_date || (formData.service_type==='transfer' && !formData.dropoff_area)) return;
    const timer = setTimeout(()=>{if(!busyRef.current) calculatePreviewRef.current(true);},500);
    return ()=>clearTimeout(timer);
  // The key includes every field sent to the read-only pricing endpoint.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[continuousLayout,currentStep,priceRequestKey]);

  // Check if order is within 48 hours
  const isWithin48Hours = () => {
    if (!formData.departure_date) return false;
    const departureDate = new Date(`${formData.departure_date}T${formData.pickup_time || "00:00"}`);
    const now = new Date();
    const diff = departureDate - now;
    return diff <= 48 * 60 * 60 * 1000; // 48 hours in milliseconds
  };

  // Submit order
  const handleSubmit = async () => {
    if (busyRef.current) return;
    if (!submissionPriceReady || expandedVehicleCount > 30) {
      alert(language === "ja" ? "すべての車両の確定料金をご確認ください。" : language === "en" ? "Please confirm a valid price for every vehicle." : language === "es" ? "Confirme un precio válido para cada vehículo." : "请先确认每台车都有有效报价。数量最多30台。");
      return;
    }
    busyRef.current = true;
    setSubmitting(true);
    setRequestError("");
    try {
      const idempotencyKey = submissionKeyRef.current || createIdempotencyKey();
      submissionKeyRef.current = idempotencyKey;
      // Determine status based on 48-hour rule
      const needsConfirmation = isWithin48Hours();
      
      const buildExtraServices = (vehicle) => vehicle.extra_services.map(svc => ({
        service_type: svc.service_type,
        price: EXTRA_SERVICES_CONFIG.find(c => c.id === svc.service_type)?.price || 0,
        quantity: svc.quantity || 1,
        description: svc.service_type === "other" ? vehicle.other_service_desc : svc.description,
        status: svc.service_type === "other" ? "pending_approval" : "approved",
      }));
      const sharedOrderData = {
        service_type: formData.service_type,
        pickup_area: formData.pickup_area === "other" ? "other" : formData.pickup_area,
        dropoff_area: formData.dropoff_area || formData.pickup_area,
        pickup_location_detail: formData.pickup_area === "other" 
          ? formData.pickup_area_other 
          : formData.pickup_location_detail,
        dropoff_location_detail: formData.dropoff_location_detail,
        departure_date: formData.departure_date,
        pickup_time: formData.pickup_time || null,
        selected_hours: formData.service_type === "charter" ? formData.selected_hours : null,
        // New end location fields
        end_location: formData.service_type === "charter" ? formData.end_location : null,
        end_location_in_base: formData.service_type === "charter" ? formData.end_location_in_base : true,
        end_location_other: formData.service_type === "charter" ? formData.end_location_other : null,
        flight_number: formData.flight_number || null,
        pickup_flight_number: continuousLayout && formData.service_type === 'charter' && isAirportArea(formData.pickup_area, areas) ? String(formData.pickup_flight_number || '').trim() || null : null,
        end_flight_number: continuousLayout && formData.service_type === 'charter' && isAirportArea(formData.end_location, [...areas, ...(endLocationOptions?.not_in_base_locations || []), ...(endLocationOptions?.in_base_locations || [])]) ? String(formData.end_flight_number || '').trim() || null : null,
        customer_code: formData.customer_code,
        customer_name: formData.customer_name,
        phone: formData.phone,
        // Set status based on 48-hour rule
        initial_status: needsConfirmation ? "pending_confirmation" : "confirmed",
      };
      const vehicles = vehiclePlans.map(vehicle => ({
        vehicle_type: vehicle.vehicle_type,
        quantity: Math.max(1, Number(vehicle.quantity) || 1),
        passenger_count: Math.max(1, Number(vehicle.passenger_count) || 1),
        luggage_count: Math.max(0, Number(vehicle.luggage_count) || 0),
        child_seat_count: Math.max(0, Number(vehicle.child_seat_count) || 0),
        extra_services: buildExtraServices(vehicle),
        other_service_desc: vehicle.other_service_desc || null,
        trip_details: appendAirportFlightNotes(vehicle.trip_details, sharedOrderData.pickup_flight_number, sharedOrderData.end_flight_number) || null,
      }));
      const useMultiVehicleEndpoint = isMultiVehicleBooking;
      const response = useMultiVehicleEndpoint
        ? await axios.post(`${API}/orders/v2/multi-vehicle`, {
            shared: sharedOrderData,
            vehicles,
            create_payment_page: createCombinedPaymentPage,
          }, {
            headers: { "Idempotency-Key": idempotencyKey },
            timeout: 60000,
          })
        : await axios.post(`${API}/orders/v2`, {
            ...sharedOrderData,
            ...vehicles[0],
            price_snapshot: vehiclePriceSnapshots[vehiclePlans[0].id],
          }, {
            headers: { "Idempotency-Key": idempotencyKey },
            timeout: 60000,
          });
      submissionKeyRef.current = null;
      
      // Show different message for quote mode
      if (useMultiVehicleEndpoint) {
        const paymentText = response.data?.payment_page_url ? `\n${response.data.payment_page_url}` : "";
        alert(`${t.orderSuccess}\n${response.data?.group_no || ""}\n${response.data?.created_count || expandedVehicleCount} ${language === "ja" ? "台" : language === "en" ? "vehicles" : language === "es" ? "vehículos" : "台车"}${paymentText}`);
      } else if (priceSnapshot?.requires_quote) {
        alert(t.orderSuccessQuote);
      } else {
        alert(t.orderSuccess);
      }
      
      // Reset form
      setCurrentStep(1);
      setFormData({
        ...formData,
        pickup_area: "",
        pickup_area_other: "",
        service_type: "",
        departure_date: "",
        pickup_time: "",
        pickup_location_detail: "",
        dropoff_location_detail: "",
        dropoff_area: "",
        flight_number: "",
        pickup_flight_number: "",
        end_flight_number: "",
        passenger_count: 1,
        luggage_count: 0,
        child_seat_count: 0,
        selected_hours: 5,
        end_location_in_base: true,
        end_location: "",
        end_location_other: "",
        extra_services: [],
        other_service_desc: "",
        vehicle_type: "alphard",
        trip_details: "",
      });
      setPriceSnapshot(null);
      setVehiclePlans([createVehiclePlan()]);
      setVehiclePriceSnapshots({});
      setCreateCombinedPaymentPage(false);
      setEndLocationOptions(null);
      
      if (onOrderCreated) onOrderCreated();
    } catch (error) {
      console.error("Failed to submit order", error);
      reportBookingFailure("submit", error);
      const submitErrorPrefix = language === "ja"
        ? "送信に失敗しました: "
        : language === "zh"
          ? "提交失败: "
          : language === "es"
            ? "No se pudo enviar la reserva: "
            : "Submission failed: ";
      setRequestError(submitErrorPrefix + bookingErrorMessage(error, language, true));
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  };

  // Get area name by language
  const getAreaName = (area) => {
    if (!area) return "";
    if (typeof area === "string") {
      const found = areas.find(a => a.code === area);
      if (!found) return area;
      area = found;
    }
    return getLocalizedAreaName(area, language);
  };

  // Get end location name
  const getEndLocationName = (code) => {
    if (!endLocationOptions) return code;
    
    // Check in base locations
    const inBase = endLocationOptions.in_base_locations?.find(l => l.code === code);
    if (inBase) return getLocalizedAreaName(inBase, language);
    
    // Check in not-in-base locations
    const notInBase = endLocationOptions.not_in_base_locations?.find(l => l.code === code);
    if (notInBase) return getLocalizedAreaName(notInBase, language);
    
    return code;
  };

  const getEndLocationTypeLabel = (returnsToBase) => {
    const configuredLabels = returnsToBase
      ? endLocationOptions?.base_label
      : endLocationOptions?.not_base_label;
    if (language === "es") {
      const baseName = getLocalizedAreaName(getSelectedBase(), "es");
      return returnsToBase ? `Regreso a ${baseName}` : `Sin regreso a ${baseName}`;
    }
    return configuredLabels?.[language]
      || configuredLabels?.en
      || (returnsToBase ? t.endInTokyo : t.endNotInTokyo);
  };

  // Check if quote mode is required
  const isQuoteMode = () => {
    // Okinawa is always quote mode
    if (formData.pickup_area === "okinawa") return true;
    if (endLocationOptions?.rule_group === "okinawa") return true;
    
    // Any region with "other" destination area
    if (formData.dropoff_area === "other") return true;
    
    // Any region with "other" end location
    if (formData.end_location === "other") return true;
    
    return endLocationOptions?.quote_mode === true;
  };

  // Get allowed charter hours for selected pickup area
  const getAllowedHours = () => {
    const area = areas.find(a => a.code === formData.pickup_area);
    return area?.allowed_charter_hours || [5, 8, 10, 12];
  };

  const getStepValidationMessage = (step = currentStep) => {
    const prefix = language === "ja"
      ? "次へ進む前に入力してください："
      : language === "en"
      ? "Please complete: "
      : language === "es"
      ? "Complete antes de continuar: "
      : "请先补充：";
    const missing = [];

    if (step === 1 && !formData.pickup_area) {
      missing.push(t.step1Title);
    }
    if (step === 2 && !formData.service_type) {
      missing.push(t.step2Title);
    }
    if (step === 3) {
      if (!formData.departure_date) missing.push(t.departureDate);
      if (formData.service_type === 'transfer') {
        if (!formData.dropoff_area) missing.push(t.destinationArea);
        if (!String(formData.pickup_location_detail || "").trim()) missing.push(t.pickupLocation);
        if (!String(formData.dropoff_location_detail || "").trim()) missing.push(t.destination);
      }
      if (formData.service_type === "charter") {
        const fields = charterFieldCopy[language] || charterFieldCopy.en;
        if (!formData.selected_hours || formData.selected_hours < 1) missing.push(t.charterHours);
        if (formData.pickup_area !== "okinawa" && !formData.dropoff_area) missing.push(continuousLayout ? fields.sightseeing : t.destinationArea);
        if (formData.dropoff_area === "other" && !String(formData.dropoff_area_other || "").trim()) missing.push(t.otherInput);
        if (!isQuoteMode() && formData.dropoff_area !== "other" && !formData.end_location) missing.push(continuousLayout ? fields.endArea : t.endLocation);
      }
    }
    if (step === 4) {
      if (!vehiclePlans.length) missing.push(t.step4Title);
      if (expandedVehicleCount > 30) missing.push(language === "ja" ? "車両は最大30台" : language === "en" ? "Maximum 30 vehicles" : language === "es" ? "Máximo 30 vehículos" : "车辆最多30台");
      if (vehiclePlans.some(vehicle => !vehicle.vehicle_type || Number(vehicle.quantity) < 1 || Number(vehicle.passenger_count) < 1)) {
        missing.push(language === "ja" ? "各車両の情報" : language === "en" ? "every vehicle's details" : language === "es" ? "los datos de cada vehículo" : "每台车的资料");
      }
    }

    return missing.length > 0 ? `${prefix}${missing.join("、")}` : "";
  };

  const nextStepValidationMessage = continuousLayout && currentStep < 5 ? [1,2,3,4].map(step => getStepValidationMessage(step)).find(Boolean) || '' : getStepValidationMessage();
  useEffect(() => {
    const total = isMultiVehicleBooking ? (multiVehiclePriceReady ? calculatedMultiVehicleTotal : null) : (priceSnapshot && !priceSnapshot.requires_quote ? Number(priceSnapshot.total_price) : null);
    onPreviewChange?.({ ...formData, vehicles: vehiclePlans, snapshots:vehiclePriceSnapshots, total, priceSnapshot, pickupAreaName:getLocalizedAreaName(areas.find(a=>a.code===formData.pickup_area)||{code:formData.pickup_area},language),dropoffAreaName:getLocalizedAreaName(areas.find(a=>a.code===formData.dropoff_area)||{code:formData.dropoff_area},language),endAreaName:getLocalizedAreaName(areas.find(a=>a.code===formData.end_location)||{code:formData.end_location},language),currentStep, canReview: !nextStepValidationMessage, loading, submitting, submitReady: submissionPriceReady, validationMessage: nextStepValidationMessage });
  }, [formData, vehiclePlans, calculatedMultiVehicleTotal, isMultiVehicleBooking, multiVehiclePriceReady, priceSnapshot, vehiclePriceSnapshots,areas,language,onPreviewChange, currentStep, nextStepValidationMessage, loading, submitting, submissionPriceReady]);

  // Navigation
  const canGoNext = () => {
    return !nextStepValidationMessage;
  };

  const goNext = async () => {
    if (busyRef.current || !canGoNext()) return;
    busyRef.current = true;
    setRequestError("");
    try {
      if ((continuousLayout || currentStep === 4) && !(await calculatePrice())) return;
      setCurrentStep(step => continuousLayout ? 5 : Math.min(5, step + 1));
    } finally {
      busyRef.current = false;
    }
  };

  const goBack = () => {
    if (busyRef.current) return;
    setRequestError("");
    setCurrentStep(continuousLayout ? 1 : currentStep - 1);
  };

  // Get vehicle name by language
  const getVehicleName = (vehicleId) => {
    const vehicle = VEHICLE_TYPES.find(v => v.id === vehicleId);
    return vehicle ? vehicle[`name_${language}`] || vehicle.name_en : vehicleId;
  };

  const customerAutofillText = {
    zh: {
      title: "可填充的客户常用信息",
      selectAll: "全选",
      clearAll: "清空",
      applySelected: "填充所选",
      noSelection: "请先勾选要填充的信息",
      orders: "历史预约",
      frequentHotel: "常用酒店/地点",
      frequentDestination: "常用目的地",
      frequentVehicle: "常用车型",
      contact: "联系人",
      phone: "电话",
      email: "邮箱",
      paymentHabit: "付款习惯",
      none: "未记录",
      card: "信用卡 / Square",
      paid: "常见为已收款",
      recorded: "有付款记录",
      unknown: "未记录",
    },
    ja: {
      title: "入力できる顧客のよく使う情報",
      selectAll: "全選択",
      clearAll: "クリア",
      applySelected: "選択項目を入力",
      noSelection: "入力する項目を選択してください",
      orders: "過去予約",
      frequentHotel: "よく使うホテル/地点",
      frequentDestination: "よく使う目的地",
      frequentVehicle: "よく使う車種",
      contact: "担当者",
      phone: "電話番号",
      email: "メール",
      paymentHabit: "支払い傾向",
      none: "記録なし",
      card: "クレジットカード / Square",
      paid: "入金済みが多い",
      recorded: "支払い記録あり",
      unknown: "記録なし",
    },
    en: {
      title: "Frequent customer details available",
      selectAll: "Select all",
      clearAll: "Clear",
      applySelected: "Fill selected",
      noSelection: "Select details to fill first",
      orders: "Past bookings",
      frequentHotel: "Frequent hotel/place",
      frequentDestination: "Frequent destination",
      frequentVehicle: "Frequent vehicle",
      contact: "Contact",
      phone: "Phone",
      email: "Email",
      paymentHabit: "Payment habit",
      none: "Not recorded",
      card: "Credit card / Square",
      paid: "Usually prepaid",
      recorded: "Payment record exists",
      unknown: "Not recorded",
    },
    es: {
      title: "Datos habituales del cliente disponibles",
      selectAll: "Seleccionar todo",
      clearAll: "Limpiar",
      applySelected: "Completar seleccionados",
      noSelection: "Seleccione primero los datos que desea completar",
      orders: "Reservas anteriores",
      frequentHotel: "Hotel/lugar habitual",
      frequentDestination: "Destino habitual",
      frequentVehicle: "Vehículo habitual",
      contact: "Contacto",
      phone: "Teléfono",
      email: "Correo electrónico",
      paymentHabit: "Forma de pago habitual",
      none: "Sin datos",
      card: "Tarjeta de crédito / Square",
      paid: "Normalmente prepagado",
      recorded: "Existe un registro de pago",
      unknown: "Sin datos",
    },
  }[language] || {};

  const customerAutofillFields = autofillNotice ? [
    {
      key: "customer_name",
      label: customerAutofillText.contact,
      value: autofillNotice.customer_name,
      apply: (draft) => ({ ...draft, customer_name: autofillNotice.customer_name }),
    },
    {
      key: "phone",
      label: customerAutofillText.phone,
      value: autofillNotice.phone,
      apply: (draft) => ({ ...draft, phone: autofillNotice.phone }),
    },
    {
      key: "pickup_location_detail",
      label: customerAutofillText.frequentHotel,
      value: autofillNotice.frequent_hotel || autofillNotice.pickup_location_detail,
      apply: (draft) => ({ ...draft, pickup_location_detail: autofillNotice.frequent_hotel || autofillNotice.pickup_location_detail }),
    },
    {
      key: "dropoff_location_detail",
      label: customerAutofillText.frequentDestination,
      value: autofillNotice.dropoff_location_detail,
      apply: (draft) => ({ ...draft, dropoff_location_detail: autofillNotice.dropoff_location_detail }),
    },
    {
      key: "vehicle_type",
      label: customerAutofillText.frequentVehicle,
      value: autofillNotice.vehicle_type,
      displayValue: autofillNotice.vehicle_type ? getVehicleName(autofillNotice.vehicle_type) : "",
      apply: (draft) => ({ ...draft, vehicle_type: autofillNotice.vehicle_type }),
    },
  ].filter((field) => String(field.value || "").trim()) : [];

  const selectedCustomerAutofillCount = customerAutofillFields.filter(
    (field) => customerAutofillSelections[field.key]
  ).length;

  const toggleCustomerAutofillField = (fieldKey) => {
    setCustomerAutofillSelections(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  const selectAllCustomerAutofillFields = () => {
    setCustomerAutofillSelections(
      Object.fromEntries(customerAutofillFields.map((field) => [field.key, true]))
    );
  };

  const clearCustomerAutofillFields = () => {
    setCustomerAutofillSelections({});
  };

  const applySelectedCustomerAutofillFields = () => {
    if (selectedCustomerAutofillCount === 0) {
      alert(customerAutofillText.noSelection);
      return;
    }

    setFormData(prev => customerAutofillFields.reduce((draft, field) => (
      customerAutofillSelections[field.key] ? field.apply(draft) : draft
    ), prev));
  };

  // Generate display name for order
  const getDisplayName = () => {
    const pickup = getAreaName(formData.pickup_area);
    if (formData.service_type === "transfer") {
      const dropoff = getAreaName(formData.dropoff_area || formData.pickup_area);
      return `${pickup} → ${dropoff} · ${t.transfer}`;
    } else {
      return `${pickup} · ${t.charter} · ${formData.selected_hours} ${t.hours}`;
    }
  };

  const aiText = {
    zh: {
      title: "AI识别订单",
      desc: "粘贴旅行社订单文本，系统只会填入能确定的字段。",
      open: "AI识别订单",
      paste: "粘贴订单内容",
      placeholder: "例如：出発日、出発時間、終了時間、行程、お客様名...",
      parse: "开始识别",
      parsing: "识别中...",
      apply: "应用到订单",
      close: "关闭",
      preview: "识别结果预览",
      empty: "暂未识别出可安全填入的字段，请手动填写。",
      safeNote: "识别不出的字段会保持空白，不会自动猜测。",
      aiFallback: "AI服务暂不可用，已使用规则识别可确定字段。",
    },
    ja: {
      title: "AI予約認識",
      desc: "旅行会社の予約テキストを貼り付けると、確定できる項目のみ入力します。",
      open: "AIで認識",
      paste: "予約内容を貼り付け",
      placeholder: "例：出発日、出発時間、終了時間、行程、お客様名...",
      parse: "認識する",
      parsing: "認識中...",
      apply: "予約に反映",
      close: "閉じる",
      preview: "認識結果",
      empty: "安全に入力できる項目は見つかりませんでした。手動で入力してください。",
      safeNote: "不明な項目は空欄のままにし、推測入力はしません。",
      aiFallback: "AIサービスが利用できないため、確定できる項目のみルールで認識しました。",
    },
    en: {
      title: "AI Order Recognition",
      desc: "Paste agency order text. Only fields that can be safely confirmed will be filled.",
      open: "AI Recognize",
      paste: "Paste order text",
      placeholder: "Example: departure date, start time, end time, itinerary, customer name...",
      parse: "Recognize",
      parsing: "Recognizing...",
      apply: "Apply to Order",
      close: "Close",
      preview: "Recognition Preview",
      empty: "No safe fields were recognized. Please fill the form manually.",
      safeNote: "Unknown fields stay blank. The system will not guess.",
      aiFallback: "AI service is unavailable, so only rule-confirmed fields were recognized.",
    },
    es: {
      title: "Reconocimiento de reservas con IA",
      desc: "Pegue el texto de la reserva de la agencia. Solo se completarán los campos que puedan confirmarse con seguridad.",
      open: "Reconocer con IA",
      paste: "Pegar texto de la reserva",
      placeholder: "Ejemplo: fecha de salida, hora de inicio, hora final, itinerario, nombre del cliente...",
      parse: "Reconocer",
      parsing: "Reconociendo...",
      apply: "Aplicar a la reserva",
      close: "Cerrar",
      preview: "Vista previa del reconocimiento",
      empty: "No se reconocieron campos seguros. Complete el formulario manualmente.",
      safeNote: "Los campos desconocidos permanecerán vacíos. El sistema no hará suposiciones.",
      aiFallback: "El servicio de IA no está disponible; solo se reconocieron los campos confirmados por reglas.",
    },
  }[language] || {};

  const aiFieldLabels = {
    service_type: t.step2Title,
    pickup_area: language === "ja" ? "乗車エリア" : language === "zh" ? "出发地区" : language === "es" ? "Zona de recogida" : "Pickup Area",
    dropoff_area: t.destinationArea,
    pickup_location_detail: t.pickupLocation,
    dropoff_location_detail: t.destination,
    departure_date: t.departureDate,
    pickup_time: t.departureTime,
    selected_hours: t.charterHours,
    end_location: t.endLocation,
    end_location_in_base: language === "ja" ? "基地に戻る" : language === "zh" ? "是否返回基地" : language === "es" ? "Regreso a la base" : "Return to base",
    vehicle_type: language === "ja" ? "車両" : language === "zh" ? "车型" : language === "es" ? "Vehículo" : "Vehicle",
    passenger_count: t.passengerCount,
    luggage_count: t.luggageCount,
    child_seat_count: t.childSeats,
    customer_code: t.customerCode,
    customer_name: t.customerName,
    phone: t.phone,
    flight_number: t.flightNumber,
    trip_details: language === "ja" ? "行程メモ" : language === "zh" ? "行程备注" : language === "es" ? "Notas del itinerario" : "Trip Notes",
  };

  const formatAiValue = (key, value) => {
    if (value === true) return language === "ja" ? "はい" : language === "zh" ? "是" : language === "es" ? "Sí" : "Yes";
    if (value === false) return language === "ja" ? "いいえ" : language === "zh" ? "否" : language === "es" ? "No" : "No";
    if (key === "service_type") return value === "charter" ? t.charter : t.transfer;
    if (["pickup_area", "dropoff_area", "end_location"].includes(key)) return getAreaName(value);
    if (key === "vehicle_type") return getVehicleName(value);
    if (key === "selected_hours") return `${value} ${t.hours}`;
    return String(value);
  };

  const getAiPreviewEntries = () => {
    const parsed = aiParseResult?.parsed || {};
    return Object.entries(parsed)
      .filter(([key, value]) => value !== undefined && value !== null && value !== "" && key !== "end_location_other")
      .map(([key, value]) => ({ key, value }));
  };

  const handleAiParse = async () => {
    const text = aiRawText.trim();
    if (!text) {
      setAiParseError(aiText.empty);
      return;
    }
    setAiParsing(true);
    setAiParseError("");
    setAiParseResult(null);
    try {
      const response = await axios.post(`${API}/orders/parse-text-ai`, {
        raw_text: text,
        pickup_area: formData.pickup_area,
        service_type: formData.service_type,
      });
      setAiParseResult(response.data);
      if (response.data?.ai_error) {
        setAiParseError(aiText.aiFallback);
      }
    } catch (error) {
      setAiParseError(error.response?.data?.detail || error.message || "AI parse failed");
    } finally {
      setAiParsing(false);
    }
  };

  const applyAiResult = () => {
    const parsed = aiParseResult?.parsed || {};
    if (!Object.keys(parsed).length) return;
    setFormData(prev => ({
      ...prev,
      ...parsed,
      passenger_count: parsed.passenger_count !== undefined ? Number(parsed.passenger_count) : prev.passenger_count,
      luggage_count: parsed.luggage_count !== undefined ? Number(parsed.luggage_count) : prev.luggage_count,
      child_seat_count: parsed.child_seat_count !== undefined ? Number(parsed.child_seat_count) : prev.child_seat_count,
      selected_hours: parsed.selected_hours !== undefined ? Number(parsed.selected_hours) : prev.selected_hours,
    }));
    setPriceSnapshot(null);
  };

  // Step labels by language
  const stepLabels = {
    1: { en: 'Pickup Area', ja: '乗車エリア', zh: '选择出发地', es: 'Recogida' },
    2: { en: 'Service Type', ja: 'サービス', zh: '单程/包车', es: 'Servicio' },
    3: { en: 'Details', ja: '詳細情報', zh: '填写信息', es: 'Detalles' },
    4: { en: 'Vehicle', ja: '車両選択', zh: '选择车型', es: 'Vehículo' },
    5: { en: 'Total', ja: '合計金額', zh: '合计金额', es: 'Total' },
  };
  const isDetailsStep = currentStep === 3;
  const continuousCopy = continuousBookingCopy[language] || continuousBookingCopy.en;
  const charterCopy = charterFieldCopy[language] || charterFieldCopy.en;
  const continuousCharter = continuousLayout && formData.service_type === 'charter';
  const airportCopy = airportFlightCopy[language] || airportFlightCopy.en;
  const pickupIsAirport = continuousCharter && isAirportArea(formData.pickup_area, areas);
  const endIsAirport = continuousCharter && isAirportArea(formData.end_location, [...areas, ...(endLocationOptions?.not_in_base_locations || []), ...(endLocationOptions?.in_base_locations || [])]);
  const charterEndAreas = [...new Map([
    ...(endLocationOptions?.in_base_locations || []),
    ...(endLocationOptions?.not_in_base_locations || []),
  ].map(area => [area.code, area])).values()];
  const editingContinuous = continuousLayout && currentStep < 5;
  const CharterSection = 'div';
  if (actionsRef) actionsRef.current = { review: goNext, back: goBack, submit: handleSubmit };
  const vehicleEditor = <VehicleStep compact={continuousLayout} formData={formData} getAvailableVehicleTypes={getAvailableVehicleTypes} getSelectedBase={getSelectedBase} language={language} t={t} vehiclePlans={vehiclePlans} expandedVehicleCount={expandedVehicleCount} onUpdateVehicle={updateVehiclePlan} onAddVehicle={addVehiclePlan} onDuplicateVehicle={duplicateVehiclePlan} onRemoveVehicle={removeVehiclePlan}/>;

  return (
    <div className={continuousLayout ? 'sp-continuous-booking' : isDetailsStep ? "rounded-[22px] bg-transparent shadow-none" : "premium-card"}>
      {continuousLayout ? <nav className="sp-booking-progress" aria-label={t.step5Title}>{['journey','vehicle','contact','review','complete'].map((key,index)=><span key={key} className={(currentStep===5 ? index===3 : index===0) ? 'is-active' : ''}><b>{index+1}</b>{continuousCopy[key]}</span>)}</nav> : <StepProgress currentStep={currentStep} language={language} stepLabels={stepLabels} />}

      {(configError || endLocationError) && (
        <div role="alert" className="p-4 text-destructive">
          <p>{requestCopy.config}</p>
          <p>{configError || endLocationError}</p>
          <button type="button" disabled={configLoading} onClick={() => {
            if (configError) fetchConfig();
            if (endLocationError) fetchEndLocationOptions(formData.pickup_area);
          }} className="underline">{requestCopy.retry}</button>
        </div>
      )}
      {requestError && <div role="alert" className="p-4 text-destructive whitespace-pre-wrap">{requestError}</div>}

      <fieldset disabled={loading || submitting} className={isDetailsStep ? "min-w-0 px-0 pb-6 pt-5 sm:px-0" : "min-w-0 p-6"}>
        {/* Step 1: Select Pickup Area */}
        {currentStep === 1 && !continuousLayout && (
          <PickupAreaStep
            areas={areas}
            formData={formData}
            getAreaName={getAreaName}
            setFormData={setFormData}
            t={t}
          />
        )}

        {/* Step 2: Select Service Type */}
        {currentStep === 2 && !continuousLayout && (
          <ServiceTypeStep formData={formData} setFormData={setFormData} t={t} />
        )}

        {/* Step 3: Order Details */}
        {(currentStep === 3 || editingContinuous) && (
          <div className="agency-details-centered mx-auto max-w-3xl">
            <div className="sp-journey-heading mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="premium-panel-title flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                {continuousLayout ? <><span className="sp-section-number">1</span>{continuousCopy.journey}</> : t.step3Title}
              </h2>
            </div>
            <div className={continuousLayout ? 'sp-continuous-fields' : 'space-y-6'}>
              <details open={onPreviewChange ? undefined : true} className="sp-booking-recognition rounded-[20px] bg-black/15 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-5">
                {onPreviewChange && <summary><Sparkles size={16} />{continuousLayout ? continuousCopy.ai : aiText.title}</summary>}
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#d8b769]/45 bg-[#f5df9a] px-3 py-1 text-xs font-semibold text-black">
                      <Sparkles className="h-3.5 w-3.5" />
                      {aiText.title}
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{aiText.desc}</p>
                  </div>
                  <p className="text-xs text-muted-foreground sm:max-w-xs sm:text-right">{aiText.safeNote}</p>
                </div>

                <label className="mb-2 block text-sm font-semibold text-foreground">{aiText.paste}</label>
                <textarea
                  value={aiRawText}
                  onChange={(e) => setAiRawText(e.target.value)}
                  rows={8}
                  className="w-full premium-input min-h-[220px] resize-y"
                  placeholder={aiText.placeholder}
                />

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={handleAiParse}
                    disabled={aiParsing}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#f5d480] px-5 py-3 text-sm font-bold text-[#120e05] shadow-lg shadow-[#d8b769]/15 transition hover:bg-[#ffe29a] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Wand2 className="h-4 w-4" />
                    {aiParsing ? aiText.parsing : aiText.parse}
                  </button>
                  {aiParseResult?.recognized?.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {aiParseResult.recognized.join(" / ")}
                    </div>
                  )}
                </div>

                {aiParseError && (
                  <div className="mt-4 rounded-xl border border-[#d8b769]/20 bg-[#d8b769]/10 p-3 text-sm font-medium text-[#f3d98b]">
                    {aiParseError}
                  </div>
                )}

                {aiParseResult && (
                  <div className="mt-4 rounded-2xl bg-white/[0.04] p-4 backdrop-blur-md">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <h4 className="font-bold text-foreground">{aiText.preview}</h4>
                      <span className="rounded-full border border-[#d8b769]/25 bg-[#d8b769]/10 px-3 py-1 text-xs font-semibold text-[#f3d98b]">
                        {aiParseResult.parse_model || "AI"}
                      </span>
                    </div>

                    {getAiPreviewEntries().length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {getAiPreviewEntries().map(({ key, value }) => (
                            <div key={key} className="rounded-xl bg-white/[0.04] p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {aiFieldLabels[key] || key}
                              </div>
                              <div className="mt-1 break-words text-sm font-semibold text-foreground">
                                {formatAiValue(key, value)}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            onClick={applyAiResult}
                            className="rounded-2xl bg-[#f5d480] px-5 py-3 text-sm font-bold text-[#120e05] shadow-lg shadow-[#d8b769]/15 transition hover:bg-[#ffe29a]"
                          >
                            {aiText.apply}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-xl bg-white/[0.04] p-4 text-sm text-muted-foreground">
                        {aiText.empty}
                      </div>
                    )}
                  </div>
                )}
              </details>
              
              {/* Selected Pickup Area Display */}
              {!continuousLayout && <div className="border-y border-[#d8b769]/15 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f5d480] text-sm font-semibold text-[#120e05] shadow-lg shadow-[#d8b769]/15">
                    1
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b769]">
                      {language === 'ja' ? '乗車エリア' : language === 'zh' ? '出发地区' : language === 'es' ? 'Zona de recogida' : 'Pickup Area'}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {formData.pickup_area === "other" 
                        ? formData.pickup_area_other || t.other
                        : getAreaName(areas.find(a => a.code === formData.pickup_area) || { code: formData.pickup_area })}
                    </p>
                  </div>
                  <div className="ml-auto rounded-full bg-[#d8b769]/10 px-3 py-1 text-xs font-semibold text-[#f3d98b]">
                    {formData.service_type === "transfer" ? t.transfer : t.charter}
                  </div>
                </div>
              </div>}
              {editingContinuous && <>
                <div className="sp-journey-service"><label>{continuousCopy.service}</label><div className="sp-service-options">{[['transfer',Plane,t.transfer],['charter',Car,t.charter]].map(([value,Icon,label])=><button type="button" key={value} aria-pressed={formData.service_type===value} onClick={()=>setFormData(prev=>({...prev,service_type:value,extra_services:value==='charter'?setExtendedWaitingQuantity(prev.extra_services,0):prev.extra_services}))}><Icon size={16}/>{label}</button>)}</div></div>
                <label className="sp-journey-origin">{continuousCopy.pickupArea}<select aria-label={continuousCopy.pickupArea} value={formData.pickup_area} onChange={event=>setFormData(prev=>({...prev,pickup_area:event.target.value}))}><option value="">{t.selectArea}</option>{areas.map(area=><option key={area.code} value={area.code}>{getAreaName(area)}</option>)}</select>{formData.pickup_area==='other' && <input aria-label={t.otherInput} value={formData.pickup_area_other} onChange={event=>setFormData(prev=>({...prev,pickup_area_other:event.target.value}))} placeholder={t.otherInput}/>}</label>
              </>}

              {/* Date and Time */}
              <div className="sp-journey-date grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      {t.departureDate}
                    </span>
                    <span className="text-destructive"> *</span>
                  </label>
                  <div
                    className="relative"
                    data-testid="departure-date-picker-shell"
                  >
                    <input
                      type="date"
                      value={formData.departure_date}
                      onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })}
                      onClick={openDatePicker}
                      required
                      className="w-full premium-input cursor-pointer pr-11"
                      data-testid="departure-date-picker"
                    />
                    <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    <span className="inline-flex items-center gap-2">
                      <Clock3 className="h-4 w-4 text-muted-foreground" />
                      {t.departureTime}
                    </span>
                    <span className="text-muted-foreground"> ({t.optional})</span>
                  </label>
                  <input
                    type="time"
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                    className="w-full premium-input"
                  />
                </div>
              </div>

              {/* Charter specific: Hours, Destination Area, and End Location */}
              {formData.service_type === "charter" && (
                <CharterSection className="sp-charter-settings">
                  {/* Okinawa Quote Mode */}
                  {endLocationOptions?.quote_mode && (
                    <div className="sp-charter-quote-notice rounded-xl border border-warning/20 bg-warning/10 px-4 py-3">
                      <h4 className="font-bold text-warning mb-2">{t.quoteMode}</h4>
                      <p className="text-sm text-warning">{t.quoteModeNote}</p>
                    </div>
                  )}
                  
                  <div className="sp-charter-hours-row grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sp-charter-hours">
                      <label className="block text-sm font-semibold text-foreground mb-1">
                        {t.charterHours} <span className="text-destructive">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          aria-label={t.charterHours}
                          aria-describedby={continuousLayout ? 'sp-charter-duration-note' : undefined}
                          min={isKansaiPremiumHiaceCharter ? "10" : "1"}
                          max={isKansaiPremiumHiaceCharter ? "10" : "24"}
                          value={formData.selected_hours}
                          onChange={(e) => {
                            const nextHours = isKansaiPremiumHiaceCharter ? 10 : (parseInt(e.target.value) || 1);
                            setFormData({ ...formData, selected_hours: nextHours });
                          }}
                          readOnly={isKansaiPremiumHiaceCharter}
                          className="w-full premium-input"
                          placeholder="6"
                        />
                        <span className="text-muted-foreground whitespace-nowrap">{t.hours}</span>
                      </div>
                      {continuousLayout && <p id="sp-charter-duration-note" className="sp-charter-duration-note">{charterCopy.durationNote}</p>}
                    </div>
                    
                    {/* Destination Area - Hide for Okinawa (quote mode), filter by base */}
                    {!continuousLayout && formData.pickup_area !== "okinawa" && (
                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1">
                          {t.destinationArea} <span className="text-destructive">*</span>
                        </label>
                        {/* Show info about region-specific destinations */}
                        {getSelectedBase() === "osaka" && (
                          <p className="text-xs text-warning mb-1">
                            {language === "zh" ? "关西地区目的地" : language === "ja" ? "関西エリアの目的地" : language === "es" ? "Destinos de la región de Kansai" : "Kansai region destinations"}
                          </p>
                        )}
                        {getSelectedBase() === "kanazawa" && (
                          <p className="text-xs text-warning mb-1">
                            {language === "zh" ? "金泽地区目的地" : language === "ja" ? "金沢エリアの目的地" : language === "es" ? "Destinos de la región de Kanazawa" : "Kanazawa region destinations"}
                          </p>
                        )}
                        <select
                          className="premium-input"
                          value={formData.dropoff_area}
                          onChange={(e) => {
                            const isOther = e.target.value === "other";
                            setFormData({ 
                              ...formData, 
                              dropoff_area: e.target.value,
                              dropoff_area_other: isOther ? "" : formData.dropoff_area_other
                            });
                          }}
                          required
                        >
                          <option value="">-- {t.selectArea} --</option>
                          {/* Filter destinations by selected base using allowed_bases */}
                          {getDestinationAreasByBase()
                            .filter(a => a.code !== 'other')
                            .map((area) => (
                              <option key={area.code} value={area.code}>
                                {getLocalizedAreaName(area, language)}
                              </option>
                            ))
                          }
                          {/* Always add 'other' option at the end for quote mode */}
                          <option value="other">
                            {language === 'ja' ? 'その他（お見積り）' : language === 'zh' ? '其他（需报价）' : language === 'es' ? 'Otro (requiere presupuesto)' : 'Other (Quote required)'}
                          </option>
                        </select>
                      </div>
                    )}
                  </div>
                  
                  {/* Any region: Other destination text input (quote mode) */}
                  {formData.dropoff_area === "other" && (
                    <div className="sp-charter-quote-location rounded-xl border border-warning/20 bg-warning/10 px-4 py-3">
                      <label className="block text-sm font-semibold text-warning mb-2">
                        {t.otherInput} <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.dropoff_area_other || ""}
                        onChange={(e) => setFormData({ ...formData, dropoff_area_other: e.target.value })}
                        className="w-full premium-input"
                        placeholder={language === "ja" ? "目的地を入力してください" : language === "zh" ? "请输入目的地地址" : language === "es" ? "Introduzca la dirección de destino" : "Enter destination address"}
                      />
                      <p className="text-sm text-warning mt-2">{t.quoteModeNote}</p>
                    </div>
                  )}
                  
                  {/* Keep the end-area control visible in the continuous charter form. */}
                  {(continuousLayout || (formData.dropoff_area &&
                   formData.dropoff_area !== "other" &&
                   endLocationOptions && 
                   !endLocationOptions.quote_mode && 
                   formData.pickup_area !== "okinawa")) && (
                    <div className={continuousLayout ? 'sp-charter-end-area' : 'space-y-4 rounded-2xl bg-black/15 p-4'}>
                      <h4 className="font-semibold text-foreground">{continuousLayout ? charterCopy.endArea : t.endLocation}</h4>
                      {continuousLayout ? <select
                        aria-label={charterCopy.endArea}
                        className="premium-input"
                        disabled={!endLocationOptions || endLocationOptions.quote_mode || Boolean(endLocationError)}
                        value={formData.end_location}
                        onChange={event => {
                          const code = event.target.value;
                          setFormData(prev => ({...prev, end_location: code,
                            end_location_in_base: Boolean(endLocationOptions?.in_base_locations?.some(area => area.code === code)),
                            end_location_other: code === 'other' ? '' : prev.end_location_other}));
                        }}
                      >
                        <option value="">-- {endLocationOptions?.quote_mode ? t.quoteMode : t.selectEndLocation} --</option>
                        {charterEndAreas.map(area => <option key={area.code} value={area.code}>{getLocalizedAreaName(area, language)}{area.extra_hours > 0 ? ` (+${area.extra_hours}h)` : ''}</option>)}
                      </select> : <>
                      
                      {/* End in Base (Tokyo/Kanazawa) */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="end_location_type"
                            checked={formData.end_location_in_base === true}
                            onChange={() => setFormData({ 
                              ...formData, 
                              end_location_in_base: true, 
                              end_location: endLocationOptions.in_base_locations[0]?.code || "",
                              end_location_other: ""
                            })}
                            className="h-4 w-4 accent-primary"
                          />
                          <span className="font-semibold">
                            {getEndLocationTypeLabel(true)}
                          </span>
                        </label>
                        {formData.end_location_in_base && (
                          <select
                            className="premium-input"
                            ref={(el) => {
                              if (el && endLocationOptions?.in_base_locations?.length > 0) {
                                const locs = endLocationOptions.in_base_locations;
                                if (el.options.length !== locs.length + 1) {
                                  while (el.options.length > 1) el.remove(1);
                                  locs.forEach((loc) => {
                                    const opt = document.createElement('option');
                                    opt.value = loc.code;
                                    opt.textContent = getLocalizedAreaName(loc, language);
                                    el.appendChild(opt);
                                  });
                                }
                              }
                            }}
                            value={formData.end_location}
                            onChange={(e) => setFormData({ ...formData, end_location: e.target.value })}
                          >
                            <option value="">-- {t.selectEndLocation} --</option>
                          </select>
                        )}
                      </div>
                      
                      {/* End not in Base */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="radio"
                            name="end_location_type"
                            checked={formData.end_location_in_base === false}
                            onChange={() => setFormData({ 
                              ...formData, 
                              end_location_in_base: false, 
                              end_location: "",
                              end_location_other: ""
                            })}
                            className="h-4 w-4 accent-primary"
                          />
                          <span className="font-semibold">
                            {getEndLocationTypeLabel(false)}
                            <span className="text-warning text-sm ml-2">({language === 'zh' ? '会加算回程时间' : language === 'ja' ? '回送時間が加算されます' : language === 'es' ? 'Se añade el tiempo de regreso' : 'Return time added'})</span>
                          </span>
                        </label>
                        {!formData.end_location_in_base && (
                          <div className="ml-8 space-y-2 max-w-[calc(100%-2rem)]">
                            <select
                              className="premium-input"
                              value={formData.end_location}
                              onChange={(e) => setFormData({ 
                                ...formData, 
                                end_location: e.target.value,
                                end_location_other: e.target.value === "other" ? "" : formData.end_location_other
                              })}
                            >
                              <option value="">-- {t.selectEndLocation} --</option>
                              {/* Dynamically add dropoff_area as first option if not in configured list */}
                              {formData.dropoff_area && 
                               formData.dropoff_area !== 'other' &&
                               !endLocationOptions?.not_in_base_locations?.some(l => l.code === formData.dropoff_area) &&
                               !endLocationOptions?.in_base_locations?.some(l => l.code === formData.dropoff_area) && (
                                <option value={formData.dropoff_area}>
                                  {getLocalizedAreaName(
                                    areas.find(a => a.code === formData.dropoff_area) || formData.dropoff_area,
                                    language
                                  )}
                                </option>
                              )}
                              {/* Configured end locations */}
                              {endLocationOptions?.not_in_base_locations?.map((loc) => (
                                <option key={loc.code} value={loc.code}>
                                  {getLocalizedAreaName(loc, language) + (loc.extra_hours > 0 ? ` (+${loc.extra_hours}h)` : '')}
                                </option>
                              ))}
                            </select>
                            
                            {/* Other - custom text input */}
                            {formData.end_location === "other" && (
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={formData.end_location_other}
                                  onChange={(e) => setFormData({ ...formData, end_location_other: e.target.value })}
                                  placeholder={t.endLocationOther}
                                  className="w-full premium-input"
                                />
                                <p className="text-sm text-warning">{t.requiresQuoteNote}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      </>}
                      {continuousLayout && formData.end_location === 'other' && <input aria-label={t.endLocationOther} value={formData.end_location_other} onChange={event => setFormData({...formData, end_location_other: event.target.value})} placeholder={t.endLocationOther} className="premium-input"/>}
                    </div>
                  )}
                  {continuousLayout && <label className="sp-charter-final-address">{charterCopy.finalAddress} <span className="text-muted-foreground">({t.optional})</span>
                    <input aria-label={charterCopy.finalAddress} className="premium-input" value={formData.dropoff_location_detail} onChange={event => setFormData({...formData, dropoff_location_detail: event.target.value})} placeholder={t.locationPlaceholder}/>
                  </label>}
                </CharterSection>
              )}

              {/* Transfer specific: Destination Area - filtered by pickup region */}
              {formData.service_type === "transfer" && (
                <div className="sp-journey-destination">
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {t.destinationArea} <span className="text-muted-foreground">({t.optional})</span>
                  </label>
                  {/* Show region info message */}
                  {getSelectedBase() === "osaka" && (
                    <p className="text-xs text-warning mb-1">
                      {language === "zh" ? "关西地区目的地" : language === "ja" ? "関西エリアの目的地" : language === "es" ? "Destinos de la región de Kansai" : "Kansai region destinations"}
                    </p>
                  )}
                  <select
                    className="premium-input"
                    value={formData.dropoff_area}
                    onChange={(e) => setFormData({ ...formData, dropoff_area: e.target.value })}
                  >
                    <option value="">-- {t.selectArea} --</option>
                    {/* Filter destinations by pickup area's region */}
                    {getDestinationAreasByBase()
                      .filter(a => a.code !== 'other')
                      .map((area) => (
                        <option key={area.code} value={area.code}>
                          {getLocalizedAreaName(area, language)}
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}

              {/* Location Details */}
              <div className="sp-journey-locations grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {t.pickupLocation} <span className="text-muted-foreground">({t.optional})</span>
                  </label>
                  {/* Show terminal dropdown for airports */}
                  {AIRPORT_TERMINALS[formData.pickup_area] ? (
                    <div className="space-y-2">
                      <select
                        className="premium-input"
                        ref={(el) => {
                          const terms = AIRPORT_TERMINALS[formData.pickup_area];
                          if (el && terms?.length > 0) {
                            if (el.options.length !== terms.length + 1) {
                              while (el.options.length > 1) el.remove(1);
                              terms.forEach((term) => {
                                const opt = document.createElement('option');
                                opt.value = term.id;
                                opt.textContent = language === 'ja' ? term.name_ja : language === 'zh' ? term.name_zh : language === 'es' ? term.name_es : term.name_en;
                                el.appendChild(opt);
                              });
                            }
                          }
                        }}
                        value={formData.pickup_terminal}
                        onChange={(e) => {
                          const terminal = e.target.value;
                          setFormData({ 
                            ...formData, 
                            pickup_terminal: terminal,
                            pickup_location_detail: terminal === 'hotel' ? '' : terminal
                          });
                        }}
                      >
                        <option value="">-- {t.selectTerminal} --</option>
                      </select>
                      {formData.pickup_terminal === 'hotel' && (
                        <input
                          type="text"
                          value={formData.pickup_location_detail}
                          onChange={(e) => setFormData({ ...formData, pickup_location_detail: e.target.value })}
                          placeholder={t.locationPlaceholder}
                          className="w-full premium-input"
                        />
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.pickup_location_detail}
                      onChange={(e) => setFormData({ ...formData, pickup_location_detail: e.target.value })}
                      placeholder={t.locationPlaceholder}
                      className="w-full premium-input"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {continuousCharter ? charterCopy.sightseeing : t.destination} <span className="text-muted-foreground">({continuousCharter && formData.pickup_area !== 'okinawa' ? '*' : t.optional})</span>
                  </label>
                  {/* Show terminal dropdown for airports in dropoff */}
                  {continuousCharter ? <select aria-label={charterCopy.sightseeing} className="premium-input" value={formData.dropoff_area} onChange={event => setFormData(prev => ({...prev, dropoff_area: event.target.value, dropoff_area_other: event.target.value === 'other' ? '' : prev.dropoff_area_other}))}>
                    <option value="">-- {t.selectArea} --</option>
                    {getDestinationAreasByBase().filter(area => area.code !== 'other').map(area => <option key={area.code} value={area.code}>{getLocalizedAreaName(area, language)}</option>)}
                    <option value="other">{t.otherInput}</option>
                  </select> : AIRPORT_TERMINALS[formData.dropoff_area] ? (
                    <div className="space-y-2">
                      <select
                        className="premium-input"
                        ref={(el) => {
                          const terms = AIRPORT_TERMINALS[formData.dropoff_area];
                          if (el && terms?.length > 0) {
                            if (el.options.length !== terms.length + 1) {
                              while (el.options.length > 1) el.remove(1);
                              terms.forEach((term) => {
                                const opt = document.createElement('option');
                                opt.value = term.id;
                                opt.textContent = language === 'ja' ? term.name_ja : language === 'zh' ? term.name_zh : language === 'es' ? term.name_es : term.name_en;
                                el.appendChild(opt);
                              });
                            }
                          }
                        }}
                        value={formData.dropoff_terminal}
                        onChange={(e) => {
                          const terminal = e.target.value;
                          setFormData({ 
                            ...formData, 
                            dropoff_terminal: terminal,
                            dropoff_location_detail: terminal === 'hotel' ? '' : terminal
                          });
                        }}
                      >
                        <option value="">-- {t.selectTerminal} --</option>
                      </select>
                      {formData.dropoff_terminal === 'hotel' && (
                        <input
                          type="text"
                          value={formData.dropoff_location_detail}
                          onChange={(e) => setFormData({ ...formData, dropoff_location_detail: e.target.value })}
                          placeholder={t.locationPlaceholder}
                          className="w-full premium-input"
                        />
                      )}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.dropoff_location_detail}
                      onChange={(e) => setFormData({ ...formData, dropoff_location_detail: e.target.value })}
                      placeholder={t.locationPlaceholder}
                      className="w-full premium-input"
                    />
                  )}
                </div>
              </div>

              {/* Transfer: Flight Number */}
              {pickupIsAirport && <label className="sp-airport-pickup-flight"><span>{airportCopy.pickup} <small className="text-muted-foreground">({t.optional})</small></span>
                <input aria-label={airportCopy.pickup} type="text" maxLength={100} className="premium-input" value={formData.pickup_flight_number} onChange={event => setFormData({...formData, pickup_flight_number: event.target.value})} placeholder="NH123"/>
              </label>}
              {endIsAirport && <label className="sp-airport-end-flight"><span>{airportCopy.end} <small className="text-muted-foreground">({t.optional})</small></span>
                <input aria-label={airportCopy.end} type="text" maxLength={100} className="premium-input" value={formData.end_flight_number} onChange={event => setFormData({...formData, end_flight_number: event.target.value})} placeholder="JL456"/>
              </label>}
              {formData.service_type === "transfer" && (
                <div className="sp-journey-flight">
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    {t.flightNumber} <span className="text-muted-foreground">({t.optional})</span>
                  </label>
                  <input
                    type="text"
                    value={formData.flight_number}
                    onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })}
                    placeholder="JL001, NH123..."
                    className="w-full premium-input"
                  />
                </div>
              )}

              {/* Passenger Info Section */}
              {!continuousLayout && <div className="border-t border-[#d8b769]/15 pt-5">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#d8b769]">
                  {language === 'ja' ? '乗客情報' : language === 'zh' ? '乘客信息' : language === 'es' ? 'Información de pasajeros' : 'Passenger Info'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">{t.passengerCount}</label>
                    <select
                      className="premium-input"
                      data-testid="passenger-count-select"
                      ref={(el) => {
                        if (el && el.options.length !== 20) {
                          while (el.options.length > 0) el.remove(0);
                          for (let i = 1; i <= 20; i++) {
                            const opt = document.createElement('option');
                            opt.value = i;
                            opt.textContent = i;
                            el.appendChild(opt);
                          }
                        }
                      }}
                      value={formData.passenger_count}
                      onChange={(e) => setFormData({ ...formData, passenger_count: parseInt(e.target.value) })}
                    >
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">{t.luggageCount}</label>
                    <select
                      className="premium-input"
                      data-testid="luggage-count-select"
                      ref={(el) => {
                        if (el && el.options.length !== 21) {
                          while (el.options.length > 0) el.remove(0);
                          for (let i = 0; i <= 20; i++) {
                            const opt = document.createElement('option');
                            opt.value = i;
                            opt.textContent = i;
                            el.appendChild(opt);
                          }
                        }
                      }}
                      value={formData.luggage_count}
                      onChange={(e) => setFormData({ ...formData, luggage_count: parseInt(e.target.value) })}
                    >
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">{t.childSeats}</label>
                    <select
                      className="premium-input"
                      data-testid="child-seat-select"
                      ref={(el) => {
                        if (el && el.options.length !== 4) {
                          while (el.options.length > 0) el.remove(0);
                          for (let i = 0; i <= 3; i++) {
                            const opt = document.createElement('option');
                            opt.value = i;
                            opt.textContent = i;
                            el.appendChild(opt);
                          }
                        }
                      }}
                      value={formData.child_seat_count}
                      onChange={(e) => setFormData({ ...formData, child_seat_count: parseInt(e.target.value) })}
                    >
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">{t.childSeatNote}</p>
                  </div>
                </div>
              </div>}

              {editingContinuous && <section className="sp-continuous-vehicle"><h2><span className="sp-section-number">2</span>{continuousCopy.vehicle}</h2>{vehicleEditor}</section>}

              {/* Customer Info Section */}
              <div className="sp-continuous-contact border-t border-[#d8b769]/15 pt-5">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#d8b769]">
                  {continuousLayout ? <><span className="sp-section-number">3</span>{continuousCopy.contact}</> : language === 'ja' ? '顧客情報' : language === 'zh' ? '客户信息' : language === 'es' ? 'Información del cliente' : 'Customer Info'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">{t.customerCode} <span className="text-muted-foreground">({t.optional})</span></label>
                    <input
                      type="text"
                      value={formData.customer_code}
                      onChange={(e) => setFormData({ ...formData, customer_code: e.target.value })}
                      placeholder="YM-01"
                      className="w-full premium-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">{t.customerName}</label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder={language === 'ja' ? 'お客様の氏名' : language === 'zh' ? '客户名字' : language === 'es' ? 'Nombre del cliente' : 'Mr. John'}
                      className="w-full premium-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1">
                      {t.phone} <span className="text-muted-foreground text-xs">({t.optional})</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="080-1234-5678"
                      className="w-full premium-input"
                    />
                  </div>
                </div>
                {autofillNotice && (
                  <div
                    className="mt-4 rounded-xl bg-[#d8b769]/10 p-4 text-sm"
                    data-testid="customer-autofill-summary"
                  >
                    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="font-semibold text-primary">
                        {customerAutofillText.title}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={selectAllCustomerAutofillFields}
                          className="rounded-md border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                          data-testid="customer-autofill-select-all"
                        >
                          {customerAutofillText.selectAll}
                        </button>
                        <button
                          type="button"
                          onClick={clearCustomerAutofillFields}
                          className="rounded-md border border-border-subtle px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-surface"
                          data-testid="customer-autofill-clear"
                        >
                          {customerAutofillText.clearAll}
                        </button>
                        <button
                          type="button"
                          onClick={applySelectedCustomerAutofillFields}
                          className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
                          data-testid="customer-autofill-apply"
                        >
                          {customerAutofillText.applySelected}
                        </button>
                      </div>
                    </div>
                    <div className="mb-3 grid gap-2 sm:grid-cols-2">
                      {customerAutofillFields.map((field) => (
                        <label
                          key={field.key}
                            className="flex cursor-pointer items-start gap-2 rounded-lg bg-black/10 p-3 text-muted-foreground"
                        >
                          <input
                            type="checkbox"
                            checked={!!customerAutofillSelections[field.key]}
                            onChange={() => toggleCustomerAutofillField(field.key)}
                            className="mt-1 h-4 w-4 rounded border-[#d8b769]/35 bg-black/40 text-primary focus:ring-2 focus:ring-ring"
                            data-testid={`customer-autofill-field-${field.key}`}
                          />
                          <span className="min-w-0">
                            <span className="block font-semibold text-foreground">{field.label}</span>
                            <span className="block truncate">{field.displayValue || field.value}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="grid gap-2 text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
                      <p>
                        <span className="text-foreground">{customerAutofillText.orders}:</span>{" "}
                        {autofillNotice.orderCount}
                      </p>
                      <p>
                        <span className="text-foreground">{customerAutofillText.email}:</span>{" "}
                        {autofillNotice.customer_email || customerAutofillText.none}
                      </p>
                      <p>
                        <span className="text-foreground">{customerAutofillText.paymentHabit}:</span>{" "}
                        {customerAutofillText[autofillNotice.paymentHabitKey] || customerAutofillText.unknown}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Trip Notes */}
              <div className="sp-continuous-notes">
                <label className="block text-sm font-semibold text-foreground mb-1">
                  {aiFieldLabels.trip_details} <span className="text-muted-foreground">({t.optional})</span>
                </label>
                <textarea
                  value={formData.trip_details}
                  onChange={(e) => setFormData({ ...formData, trip_details: e.target.value })}
                  rows={4}
                  className="w-full premium-input min-h-[110px]"
                  placeholder={language === "ja" ? "行程やご要望を入力してください" : language === "zh" ? "可填写行程或特别要求" : language === "es" ? "Introduzca el itinerario o solicitudes especiales" : "Enter itinerary or special requests"}
                />
              </div>

              {/* Extra Services */}
              {!continuousLayout && <div>
                <label className="block text-sm font-semibold text-foreground mb-2">{t.extraServices}</label>
                <div className="space-y-2">
                  {visibleExtraServices.map((service) => (
                    <div key={service.id}>
                      <label className="flex cursor-pointer items-center border-b border-[#d8b769]/10 py-3 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={formData.extra_services.some(s => s.service_type === service.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                extra_services: [...formData.extra_services, { service_type: service.id, quantity: 1 }]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                extra_services: formData.extra_services.filter(s => s.service_type !== service.id),
                                other_service_desc: service.id === "other" ? "" : formData.other_service_desc
                              });
                            }
                          }}
                          className="mr-3"
                        />
                        <span className="flex-1">{service[`name_${language}`] || service.name_en}</span>
                        <span className="text-muted-foreground">
                          {service.price > 0 ? `¥${service.price.toLocaleString()}` : service.id === "other" 
                            ? (language === 'ja' ? '要確認' : language === 'zh' ? '需确认' : language === 'es' ? 'Por confirmar' : 'TBD')
                            : (language === 'ja' ? '無料' : language === 'zh' ? '免费' : language === 'es' ? 'Gratis' : 'Free')}
                        </span>
                      </label>
                      {/* Show text input for "other" service when checked */}
                      {service.id === "infant_seat" && formData.extra_services.some(s => s.service_type === service.id) && (
                        <input
                          type="number" min="1" max="10" step="1"
                          aria-label={service[`name_${language}`] || service.name_en}
                          className="premium-input mt-2 w-24"
                          value={formData.extra_services.find(s => s.service_type === service.id)?.quantity || 1}
                          onChange={e => setFormData(prev => ({ ...prev, extra_services: prev.extra_services.map(s => s.service_type === service.id ? { ...s, quantity: Math.max(1, Math.min(10, Math.trunc(Number(e.target.value) || 1))) } : s) }))}
                        />
                      )}
                      {service.id === "other" && formData.extra_services.some(s => s.service_type === "other") && (
                        <div className="mt-2 ml-8">
                          <input
                            type="text"
                            value={formData.other_service_desc}
                            onChange={(e) => setFormData({ ...formData, other_service_desc: e.target.value })}
                            placeholder={language === 'ja' ? 'ご要望をご記入ください' : language === 'zh' ? '请填写您的需求' : language === 'es' ? 'Describa su solicitud' : 'Please describe your request'}
                            className="w-full premium-input text-sm"
                          />
                          <p className="mt-1 flex items-center gap-1 text-xs text-warning">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {language === 'ja' ? '※管理者が確認後、価格をお知らせします' : language === 'zh' ? '※管理员确认后会通知您价格' : language === 'es' ? '※El administrador confirmará el precio' : '※Price will be confirmed by admin'}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>}

              {/* Agency Info - Auto-filled with user's company/name */}
              <div className="sp-booking-agency flex items-center gap-3 border-t border-[#d8b769]/15 pt-4">
                <div className="w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center text-primary-foreground font-bold">
                  {(user?.name || user?.email || 'A').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {language === 'ja' ? '代理店' : language === 'zh' ? '代理店' : language === 'es' ? 'Agencia' : 'Agency'}
                  </p>
                  <p className="font-semibold">{user?.name || user?.email || ''}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Select Vehicle */}
        {currentStep === 4 && !continuousLayout && (
          <VehicleStep
            formData={formData}
            getAvailableVehicleTypes={getAvailableVehicleTypes}
            getSelectedBase={getSelectedBase}
            language={language}
            setFormData={setFormData}
            t={t}
            vehiclePlans={vehiclePlans}
            expandedVehicleCount={expandedVehicleCount}
            onUpdateVehicle={updateVehiclePlan}
            onAddVehicle={addVehiclePlan}
            onDuplicateVehicle={duplicateVehiclePlan}
            onRemoveVehicle={removeVehiclePlan}
          />
        )}

        {/* Step 5: Confirm Order */}
        {currentStep === 5 && (
          <div>
            <h2 className="premium-panel-title flex items-center gap-2 mb-6">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              {t.step5Title}
            </h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
                <p className="mt-4 text-muted-foreground">{language === 'ja' ? '計算中...' : language === 'zh' ? '计算中...' : language === 'es' ? 'Calculando...' : 'Calculating...'}</p>
              </div>
            ) : (
              <div className="max-w-2xl space-y-6">
                {/* Order Summary */}
                <div className="premium-card p-6">
                  <h3 className="font-bold text-lg mb-4">{getDisplayName()}</h3>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{t.departureDate}:</span>
                      <span className="ml-2 font-medium">{formData.departure_date}</span>
                    </div>
                    {formData.pickup_time && (
                      <div>
                        <span className="text-muted-foreground">{t.departureTime}:</span>
                        <span className="ml-2 font-medium">{formData.pickup_time}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="premium-card p-6 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-bold text-lg">{language === "ja" ? `車両一覧（${expandedVehicleCount}台）` : language === "en" ? `Vehicle list (${expandedVehicleCount})` : language === "es" ? `Lista de vehículos (${expandedVehicleCount})` : `车辆清单（${expandedVehicleCount}台）`}</h3>
                    <span className="text-xl font-bold text-success">¥{calculatedMultiVehicleTotal.toLocaleString()}</span>
                  </div>
                  {vehiclePlans.map((vehicle, index) => {
                    const snapshot = vehiclePriceSnapshots[vehicle.id];
                    const subtotal = (Number(snapshot?.total_price) || 0) * Math.max(1, Number(vehicle.quantity) || 1);
                    return (
                      <div key={vehicle.id} className="rounded-xl border border-border-subtle bg-surface/70 p-4 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-semibold">#{index + 1} · {getVehicleName(vehicle.vehicle_type)} × {vehicle.quantity}</div>
                          <div className={snapshot?.requires_quote || !snapshot ? "font-semibold text-warning" : "font-semibold text-success"}>
                            {snapshot?.requires_quote || !snapshot ? t.requiresQuote : `¥${subtotal.toLocaleString()}`}
                          </div>
                        </div>
                        <div className="mt-2 text-muted-foreground">{t.passengerCount} {vehicle.passenger_count} · {t.luggageCount} {vehicle.luggage_count} · {t.childSeats} {vehicle.child_seat_count}</div>
                        <div className="mt-1 text-muted-foreground">
                          {t.extraServices}: {vehicle.extra_services.length
                            ? vehicle.extra_services.map(service => EXTRA_SERVICES_CONFIG.find(config => config.id === service.service_type)?.[`name_${language}`] || service.service_type).join(" / ")
                            : "—"}
                        </div>
                        {snapshot && !snapshot.requires_quote && (
                          <div className="mt-1 text-muted-foreground">
                            {language === "ja" ? "1台あたり" : language === "en" ? "Unit price" : language === "es" ? "Precio unitario" : "单价"}: ¥{Number(snapshot.total_price || 0).toLocaleString()}
                            {Number(vehicle.quantity || 1) > 1 ? ` · ${language === "ja" ? "小計" : language === "en" ? "Subtotal" : language === "es" ? "Subtotal" : "小计"}: ¥${subtotal.toLocaleString()}` : ""}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(expandedVehicleCount > 1 || vehiclePlans.length > 1) && (
                    <label className="flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4 text-sm">
                      <input type="checkbox" className="mt-1 h-4 w-4" checked={createCombinedPaymentPage} onChange={event => setCreateCombinedPaymentPage(event.target.checked)} />
                      <span>{language === "ja" ? "すべての車両をまとめたオンライン決済リンクを作成" : language === "en" ? "Create one combined online payment link for all vehicles" : language === "es" ? "Crear un único enlace de pago para todos los vehículos" : "为全部车辆创建一个合并在线付款链接"}</span>
                    </label>
                  )}
                </div>

                {/* Price Breakdown */}
                {priceSnapshot && (
                  <div className="premium-card border border-border-subtle p-6">
                    <h3 className="font-bold text-lg mb-4">{t.priceBreakdown}</h3>
                    
                    {/* Time Calculation Details for Charter */}
                    {formData.service_type === "charter" && priceSnapshot.raw_billable_hours && (
                      <div className="mb-4 p-3 premium-card border border-border-subtle bg-surface/80 text-sm">
                        <div className="flex items-center gap-2 font-semibold text-foreground mb-2">
                          <CircleHelp className="h-4 w-4 text-primary" />
                          {language === 'ja' ? '時間計算' : language === 'zh' ? '时间计算' : language === 'es' ? 'Cálculo del tiempo' : 'Time Calculation'}
                        </div>
                        <div className="space-y-1 text-muted-foreground">
                          {priceSnapshot.pickup_extra_hours > 0 && (
                            <div className="flex justify-between">
                              <span>{t.pickupExtraTime}:</span>
                              <span>+{priceSnapshot.pickup_extra_hours}h</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span>{t.charterHours}:</span>
                            <span>{priceSnapshot.selected_hours}h</span>
                          </div>
                          {priceSnapshot.end_location_extra_hours > 0 && (
                            <div className="flex justify-between">
                              <span>{t.endExtraTime}:</span>
                              <span>+{priceSnapshot.end_location_extra_hours}h</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold border-t border-border-subtle pt-1 mt-1">
                            <span>{language === 'ja' ? '計費時間' : language === 'zh' ? '计费时间' : language === 'es' ? 'Tiempo facturable' : 'Billable Time'}:</span>
                            <span>{priceSnapshot.raw_billable_hours}h → {priceSnapshot.package_hours_charged}h {language === 'ja' ? 'パッケージ' : language === 'zh' ? '套餐' : language === 'es' ? 'paquete' : 'Package'}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quote Mode Notice */}
                    {priceSnapshot.requires_quote && (
                      <div className="mb-4 rounded-2xl border border-cyan-400/25 bg-cyan-400/10 p-3">
                        <div className="font-semibold text-cyan-100">
                          {t.confirmWithTony}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2 text-sm">
                      {priceSnapshot.package_price > 0 && (
                        <div className="flex justify-between">
                          <span>{t.basePrice} ({priceSnapshot.package_hours_charged}h)</span>
                          <span>¥{priceSnapshot.package_price?.toLocaleString()}</span>
                        </div>
                      )}
                      {priceSnapshot.route_pair_price > 0 && (
                        <div className="flex justify-between">
                          <span>{t.basePrice}</span>
                          <span>¥{priceSnapshot.route_pair_price?.toLocaleString()}</span>
                        </div>
                      )}
                      {priceSnapshot.overtime_price > 0 && (
                        <div className="flex justify-between text-warning">
                          <span>{t.overtimePrice} ({priceSnapshot.overtime_hours}h)</span>
                          <span>¥{priceSnapshot.overtime_price?.toLocaleString()}</span>
                        </div>
                      )}
                      {priceSnapshot.night_surcharge > 0 && (
                        <div className="flex justify-between text-primary">
                          <span>{t.nightSurcharge}</span>
                          <span>¥{priceSnapshot.night_surcharge?.toLocaleString()}</span>
                        </div>
                      )}
                      {priceSnapshot.child_seat_price > 0 && (
                        <div className="flex justify-between">
                          <span>{t.childSeatPrice}</span>
                          <span>¥{priceSnapshot.child_seat_price?.toLocaleString()}</span>
                        </div>
                      )}
                      {priceSnapshot.extra_services_price > 0 && (
                        <div className="flex justify-between">
                          <span>{t.extraServicePrice}</span>
                          <span>¥{priceSnapshot.extra_services_price?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-3 border-t border-border-subtle text-xl font-bold text-success">
                        <span>{t.totalPrice}</span>
                        {priceSnapshot.requires_quote ? (
                          <span className="text-warning text-base">
                            {t.confirmWithTony}
                          </span>
                        ) : (
                          <span>¥{priceSnapshot.total_price?.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 48-hour Warning */}
                {isWithin48Hours() && (
                  <div className="premium-card border-warning/40 bg-warning/10 p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-warning" />
                      <div>
                        <div className="font-bold text-warning">{t.pendingConfirmation}</div>
                        <div className="text-sm text-warning/90">{t.pendingConfirmationNote}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!continuousLayout && <FormNavigation
          canGoNext={canGoNext}
          currentStep={currentStep}
          goBack={goBack}
          goNext={goNext}
          handleSubmit={handleSubmit}
          loading={loading}
          nextStepValidationMessage={nextStepValidationMessage}
          submitting={submitting}
          submitDisabled={!submissionPriceReady || expandedVehicleCount > 30}
          t={t}
        />}
      </fieldset>
    </div>
  );
}
